import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { jwtDecode } from "jwt-decode";
import { Customer, User } from "@/api.types";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { logger } from "../logger";

interface SessionContextType {
  token: string | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  user: User | null;
  customer: Customer | null;
  roles: string[];
  hasRole: (role: string | string[]) => boolean;
  logout: () => void;
  login: () => void;
}

type JwtPayload = Record<string, string | string[] | undefined | null>;

const AUTH0_NAMESPACE = import.meta.env.VITE_AUTH0_NAMESPACE;
const VALID_ROLES = ["CUSTOMER"];
const AUTH_STATE_KEY = "ezx_auth_state";

const SessionContext = createContext<SessionContextType | undefined>(undefined);

const fetchUser = async (userId: string, token: string) => {
  const response = await axios.get<User>(
    `${import.meta.env.VITE_API_URL}/api/v1/users/${userId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

// Add type for API errors
interface ApiError {
  error: string;
  details?: string;
}

const fetchCustomer = async (
  customerId: string,
  token: string
): Promise<Customer> => {
  try {
    const response = await axios.get<Customer>(
      `${import.meta.env.VITE_API_URL}/api/v1/customers/${customerId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError<ApiError>(error)) {
      throw new Error(error.response?.data.details ?? error.message);
    }
    throw new Error("Unknown error occurred");
  }
};

// Auth state to persist during page reloads
interface AuthState {
  isAuthenticated: boolean;
  lastAuthenticated: number;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const {
    isLoading: isAuth0Loading,
    isAuthenticated,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [decodedToken, setDecodedToken] = useState<JwtPayload | null>(null);

  // Initialize with previously persisted auth state to prevent unnecessary redirects
  const [isPreviouslyAuthenticated, setIsPreviouslyAuthenticated] = useState(
    () => {
      try {
        const storedState = localStorage.getItem(AUTH_STATE_KEY);
        if (storedState) {
          const authState = JSON.parse(storedState) as AuthState;
          // Check if the stored auth state is still valid (less than 12 hours old)
          const isStillValid =
            authState.isAuthenticated &&
            Date.now() - authState.lastAuthenticated < 12 * 60 * 60 * 1000;
          return isStillValid;
        }
      } catch (error) {
        console.error("Failed to parse auth state from localStorage", error);
      }
      return false;
    }
  );

  // Function to persist auth state to localStorage
  const persistAuthState = useCallback((isAuthenticated: boolean) => {
    try {
      const authState: AuthState = {
        isAuthenticated,
        lastAuthenticated: Date.now(),
      };
      localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(authState));
      setIsPreviouslyAuthenticated(isAuthenticated);
    } catch (error) {
      console.error("Failed to persist auth state to localStorage", error);
    }
  }, []);

  const logout = useCallback(() => {
    persistAuthState(false);
    void auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  }, [auth0Logout, persistAuthState]);

  // Modify the token fetching effect
  useEffect(() => {
    if (isAuth0Loading) {
      return;
    }

    if (!isAuthenticated) {
      setIsLoading(false);
      setToken(null);
      setDecodedToken(null);

      // Only update persisted state if we're certain authentication has failed
      // Don't update during initial page load to prevent premature redirects
      if (!isAuth0Loading) {
        persistAuthState(false);
      }
      return;
    }

    const getToken = async () => {
      try {
        const token = await getAccessTokenSilently();
        const decoded = jwtDecode<JwtPayload>(token);
        setToken(token);
        setDecodedToken(decoded);
        persistAuthState(true);
      } catch (error) {
        logger.error("Error getting token:", error);
        persistAuthState(false);
        void logout();
      } finally {
        setIsLoading(false);
      }
    };

    void getToken();
  }, [
    isAuthenticated,
    getAccessTokenSilently,
    isAuth0Loading,
    logout,
    persistAuthState,
  ]);

  const { roles, customerId, userId, isUserValid } = useMemo(() => {
    const userId = decodedToken?.[`${AUTH0_NAMESPACE}/userId`] as string;
    const customerId = decodedToken?.[
      `${AUTH0_NAMESPACE}/customerId`
    ] as string;
    const roles =
      (decodedToken?.[`${AUTH0_NAMESPACE}/roles`] as string[]) ?? [];
    const isUserValid = roles.some((role) => VALID_ROLES.includes(role));

    return { roles, customerId, userId, isUserValid };
  }, [decodedToken]);

  // Fetch user data
  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
  } = useQuery({
    queryKey: ["user", userId, token],
    queryFn: () => fetchUser(userId, token!),
    enabled: !!userId && !!token && isUserValid,
  });

  // Fetch customer data
  const { data: customer, isLoading: isCustomerLoading } = useQuery({
    queryKey: ["customer", customerId, token],
    queryFn: () => fetchCustomer(customerId, token!),
    enabled: !!customerId && !!token && isUserValid,
  });

  const hasRole = useCallback(
    (requiredRoles: string | string[]) => {
      const roleArray = Array.isArray(requiredRoles)
        ? requiredRoles
        : [requiredRoles];
      return roleArray.some((role) => roles.includes(role));
    },
    [roles]
  );

  // Handle errors
  useEffect(() => {
    if (userError) {
      logger.error("Session error:", { userError });
      void logout();
    }
  }, [userError, logout]);

  const isInitializing =
    isAuth0Loading || isLoading || isUserLoading || isCustomerLoading;

  // The key change - we consider a user logged in if they were previously authenticated
  // and we're still initializing the session
  const isUserLoggedIn = isAuthenticated
    ? !!token && true
    : isInitializing && isPreviouslyAuthenticated;

  return (
    <SessionContext.Provider
      value={{
        isLoading: isInitializing,
        isLoggedIn: isUserLoggedIn,
        user: user ?? null,
        customer: customer ?? null,
        roles,
        token,
        hasRole,
        login: () => void loginWithRedirect(),
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}

export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
}
